import type { ManageStoresSopSection } from "../types";

export const MANAGE_STORES_SOP_SECTIONS: ManageStoresSopSection[] = [
  {
    id: "before-you-start",
    titleKey: "manage-stores-sop-before-start-title",
    titleFallback: "Before you start",
    introKey: "manage-stores-sop-before-start-intro",
    introFallback:
      "In Kivunova FMS, a store is one shop or branch in your business. Stock, staff, customers, and suppliers can all be linked to a store so each location runs smoothly on its own.",
    steps: [
      {
        bodyKey: "manage-stores-sop-before-start-1",
        bodyFallback:
          "This guide is for company admins and store managers. Company admins can work across all stores. Store managers usually see only the store they are assigned to."
      },
      {
        bodyKey: "manage-stores-sop-before-start-2",
        bodyFallback:
          "Sign in with an account that has permission to open the Stores page. If you do not see Stores in the left menu, ask your company admin to check your role."
      },
      {
        bodyKey: "manage-stores-sop-before-start-3",
        bodyFallback:
          "From here you can add new stores, review store details, and manage the people and contacts linked to each location."
      }
    ]
  },
  {
    id: "open-stores-page",
    titleKey: "manage-stores-sop-open-page-title",
    titleFallback: "Open the Stores page",
    introKey: "manage-stores-sop-open-page-intro",
    introFallback:
      "The Store List is your home base. It shows every store in your company and gives you quick access to each location.",
    steps: [
      {
        bodyKey: "manage-stores-sop-open-page-1",
        bodyFallback: "After you sign in, open Stores from the left menu."
      },
      {
        bodyKey: "manage-stores-sop-open-page-2",
        bodyFallback:
          "You will see the Store List with each store's ID, name, and status.",
        imageId: "store-list"
      },
      {
        bodyKey: "manage-stores-sop-open-page-3",
        bodyFallback:
          "Use the search box to find a store by name. Click the view icon in the Actions column to open one store."
      }
    ]
  },
  {
    id: "add-store",
    titleKey: "manage-stores-sop-add-store-title",
    titleFallback: "Add a new store",
    introKey: "manage-stores-sop-add-store-intro",
    introFallback:
      "Create a new store when you open a new branch or location. Company admins can add stores. Store managers usually manage an existing store rather than creating a new one.",
    steps: [
      {
        bodyKey: "manage-stores-sop-add-store-1",
        bodyFallback:
          "On the Store List page, click the purple Add Store button."
      },
      {
        bodyKey: "manage-stores-sop-add-store-2",
        bodyFallback:
          "Enter the store name. This is the name your team will see when they select a location.",
        imageId: "add-store"
      },
      {
        bodyKey: "manage-stores-sop-add-store-3",
        bodyFallback:
          "Add contact details: email and phone number. These help your team reach the store when needed."
      },
      {
        bodyKey: "manage-stores-sop-add-store-4",
        bodyFallback:
          "Fill in the store address. Choose the province and complete the remaining address fields for your country."
      },
      {
        bodyKey: "manage-stores-sop-add-store-5",
        bodyFallback:
          "Click Add Store to save. The new store will appear in your list. You can then add employees, customers, and suppliers for that location."
      }
    ]
  },
  {
    id: "store-details",
    titleKey: "manage-stores-sop-details-title",
    titleFallback: "View and update store details",
    introKey: "manage-stores-sop-details-intro",
    introFallback:
      "Each store has its own detail page. Here you can review the store profile and make changes when contact details or leadership change.",
    steps: [
      {
        bodyKey: "manage-stores-sop-details-1",
        bodyFallback:
          "From the Store List, click the view icon for the store you want to open."
      },
      {
        bodyKey: "manage-stores-sop-details-2",
        bodyFallback:
          "On the Details tab you will see the store name, manager, status, contact details, and full address.",
        imageId: "store-details"
      },
      {
        bodyKey: "manage-stores-sop-details-3",
        bodyFallback:
          "Click Edit to update store information such as the name, email, phone, or address."
      },
      {
        bodyKey: "manage-stores-sop-details-4",
        bodyFallback:
          "Click Update Manager when you need to assign or change the person who leads this store."
      },
      {
        bodyKey: "manage-stores-sop-details-5",
        bodyFallback:
          "Use Delete only when you are sure the store should be removed. This is usually a company admin task."
      }
    ]
  },
  {
    id: "store-employees",
    titleKey: "manage-stores-sop-employees-title",
    titleFallback: "Manage store employees",
    introKey: "manage-stores-sop-employees-intro",
    introFallback:
      "Each store has its own staff list. People added here work at that location and can be given roles such as store admin or inventory clerk.",
    steps: [
      {
        bodyKey: "manage-stores-sop-employees-1",
        bodyFallback: "Open a store, then tap the Employees tab."
      },
      {
        bodyKey: "manage-stores-sop-employees-2",
        bodyFallback:
          "You will see everyone assigned to this store, with their name, email, and role.",
        imageId: "store-employees"
      },
      {
        bodyKey: "manage-stores-sop-employees-3",
        bodyFallback:
          "Click Add New Employee to bring someone onto this store's team. Fill in their details, choose their role, then save."
      },
      {
        bodyKey: "manage-stores-sop-employees-4",
        bodyFallback:
          "To update someone, click the view icon in the Actions column, then use Edit on their profile page."
      },
      {
        bodyKey: "manage-stores-sop-employees-5",
        bodyFallback:
          "To manage employees across the whole company, use the Company page instead. That is covered in How to manage your company in Run books."
      }
    ]
  },
  {
    id: "store-customers",
    titleKey: "manage-stores-sop-customers-title",
    titleFallback: "Manage store customers",
    introKey: "manage-stores-sop-customers-intro",
    introFallback:
      "Customers are the people or businesses who buy from you. Keeping them on file for each store makes checkout, invoicing, and follow-up much faster.",
    steps: [
      {
        bodyKey: "manage-stores-sop-customers-1",
        bodyFallback: "Open a store, then tap the Customers tab."
      },
      {
        bodyKey: "manage-stores-sop-customers-2",
        bodyFallback:
          "The list shows each customer's name, phone, email, and type (for example, individual or business).",
        imageId: "store-customers"
      },
      {
        bodyKey: "manage-stores-sop-customers-3",
        bodyFallback:
          "Click Add New Customer to register someone new for this store."
      },
      {
        bodyKey: "manage-stores-sop-customers-4",
        bodyFallback:
          "Enter the customer's name, choose the customer type, then add phone and email.",
        imageId: "add-customer"
      },
      {
        bodyKey: "manage-stores-sop-customers-5",
        bodyFallback:
          "Add the address details, then click Create Customer. The customer will be ready to select during sales at this store."
      },
      {
        bodyKey: "manage-stores-sop-customers-6",
        bodyFallback:
          "Use the search box to find a customer quickly. Use the actions menu on each row to view or update an existing customer."
      }
    ]
  },
  {
    id: "store-suppliers",
    titleKey: "manage-stores-sop-suppliers-title",
    titleFallback: "Manage store suppliers",
    introKey: "manage-stores-sop-suppliers-intro",
    introFallback:
      "Suppliers are the businesses you buy stock from. Linking them to a store keeps purchasing organised and helps your team know who supplies each location.",
    steps: [
      {
        bodyKey: "manage-stores-sop-suppliers-1",
        bodyFallback: "Open a store, then tap the Suppliers tab."
      },
      {
        bodyKey: "manage-stores-sop-suppliers-2",
        bodyFallback:
          "The list shows each supplier's name, phone, email, and status.",
        imageId: "store-suppliers"
      },
      {
        bodyKey: "manage-stores-sop-suppliers-3",
        bodyFallback:
          "Click Add New Supplier to register a vendor for this store."
      },
      {
        bodyKey: "manage-stores-sop-suppliers-4",
        bodyFallback:
          "Enter the supplier name, then add a phone number or email (at least one contact method is needed).",
        imageId: "add-supplier"
      },
      {
        bodyKey: "manage-stores-sop-suppliers-5",
        bodyFallback:
          "Complete the address fields, then click Create Supplier. You can link this supplier when adding products or recording stock."
      },
      {
        bodyKey: "manage-stores-sop-suppliers-6",
        bodyFallback:
          "Use the search box to find a supplier. Use the actions menu on each row to view or update an existing supplier."
      }
    ]
  }
];
